import { useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import Icon from '@components/icons/Icon';
import styles from '@components/Aside/Aside.styles';
import Button from '@components/Button/Button';
import { TButtonVariantColor, TButtonVariantBackgroundColor } from '@components/Button/typescript';
import useBodyOverflow from '@hooks/useBodyOverflow';
import Heading from '@components/Heading/Heading';
export type AsidePropsType = {
  title?: string;
  children: React.ReactNode;
  active: boolean;
  buttonCloseVariantColor?: TButtonVariantColor;
  buttonCloseVariantBackgroundColor?: TButtonVariantBackgroundColor;
  useCurtain?: boolean;
  size?: 'default' | 'large' | 'frontConfigurator' | 'medium';
  hiddenButtonClose?: boolean;
  overflowY?: string;
  overflowX?: string;
  onCloseAside: (...args: unknown[]) => void;
};
const Aside = ({
  title = undefined,
  children,
  active,
  buttonCloseVariantColor = undefined,
  buttonCloseVariantBackgroundColor = undefined,
  useCurtain = true,
  size = 'default',
  hiddenButtonClose = false,
  overflowY = 'auto',
  overflowX = 'hidden',
  onCloseAside
}: AsidePropsType): JSX.Element => {
  const {
    t
  } = useTranslation('common');
  const asideRef = useRef<HTMLElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);

  // Gdy aside jest zamknięty – pomijaj cały overlay w kolejności Tab (WCAG)
  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    if (active) {
      el.removeAttribute('inert');
    } else {
      el.setAttribute('inert', '');
    }
  }, [active]);
  const onOverlayClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (event.target === event.currentTarget && event.button === 0) onCloseAside(event);
  };
  useBodyOverflow(active, 'aside', true, true);

  // Focus trap - WCAG 2.1.2 No Keyboard Trap (Level A)
  useEffect(() => {
    if (!active || !asideRef.current) return;

    // Save the element that had focus before opening aside
    previousActiveElementRef.current = document.activeElement as HTMLElement;

    // Get all focusable elements in the aside
    const getFocusableElements = (): HTMLElement[] => {
      if (!asideRef.current) return [];
      const focusableSelectors = ['button:not([disabled])', 'a[href]', 'input:not([disabled])', 'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])', '[role="button"]:not([aria-disabled="true"])', '[role="link"]', '[role="menuitem"]', '[role="tab"]'].join(', ');
      const elements = Array.from(asideRef.current.querySelectorAll<HTMLElement>(focusableSelectors));

      // Filter out elements that are not visible or not focusable
      return elements.filter(el => {
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' && !el.hasAttribute('disabled') && el.tabIndex !== -1;
      });
    };

    // Focus the first focusable element
    const focusableElements = getFocusableElements();
    if (focusableElements.length > 0) {
      // Small delay to ensure aside is fully rendered
      setTimeout(() => {
        focusableElements[0]?.focus();
      }, 100);
    }

    // Handle keyboard navigation
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseAside(e);
        // Restore focus to the element that opened the aside
        if (previousActiveElementRef.current) {
          previousActiveElementRef.current.focus();
        }
        return;
      }
      if (e.key !== 'Tab') return;
      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
      if (e.shiftKey) {
        // Shift + Tab - navigate backwards
        if (currentIndex === 0 || document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab - navigate forwards
        if (currentIndex === focusableElements.length - 1 || document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus when aside closes
      if (previousActiveElementRef.current && document.activeElement === document.body) {
        previousActiveElementRef.current.focus();
      }
    };
  }, [active, onCloseAside]);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: overlay click handler
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: overlay click handler
    <div ref={overlayRef} data-cy="CartSideBar" sx={styles.overlay(active, useCurtain)} onMouseDown={onOverlayClick}>
      <aside ref={asideRef} sx={styles.aside(active, size || 'default', overflowY, overflowX)} role="dialog" aria-modal="true" aria-hidden={!active}>
        <div sx={styles.asideContent}>
          {title && <div sx={styles.asideTitleContainer}>
              <Heading h={4} headingType={4} bold>
                {title}
              </Heading>
            </div>}
          {!hiddenButtonClose && <Button variant="iconOnly" type="button" addSx={styles.asideCloseButton} onClick={onCloseAside} cy="CloseAsideCartButton" variantColor={buttonCloseVariantColor} variantBackgroundColor={buttonCloseVariantBackgroundColor} aria-label={t('close')}>
              <Icon size={['24px', '24px', '24px', '24px', '24px', '32px']} icon="Close3" />
            </Button>}
          {children}
        </div>
      </aside>
    </div>
  );
};
export default Aside;